import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Upload,
  Search,
  Trash2,
  Edit,
  Image as ImageIcon,
  FileSpreadsheet,
  Loader2,
  FolderOpen,
} from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/store/useStore";
import { cn } from "@/lib/utils";
import { extractDominantColor } from "@/lib/extractColor";
import { api, mediaUrl } from "@/lib/api";
import { MediaPicker } from "@/components/MediaPicker";

const SPORTS = ["Football", "Basketball", "Tennis", "Cricket", "Rugby", "Hockey", "Volleyball", "Baseball", "Gaelic Football", "Hurling", "Camogie"];

export function TeamsPage() {
  const { teams, addTeam, updateTeam, deleteTeam, importTeams } = useStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    sport: "",
    logo: "",
    primaryColor: "#1e3a5f",
  });
  const fileInputRef = useRef(null);

  // CSV bulk import state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [csvFileName, setCsvFileName] = useState("");
  const [csvRows, setCsvRows] = useState([]); // [{name, sport, logo_url, primaryColor, league_slug}]
  const [csvErrors, setCsvErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ phase: "", done: 0, total: 0 });
  const [defaultSport, setDefaultSport] = useState("Soccer");
  const [autoCreateLeagues, setAutoCreateLeagues] = useState(true);
  const [defaultLeagueLogoUrl, setDefaultLeagueLogoUrl] = useState("");
  const [defaultLeagueColor, setDefaultLeagueColor] = useState("#c8102e");
  const [defaultLeagueSecondaryColor, setDefaultLeagueSecondaryColor] = useState("#0033a0");
  const csvInputRef = useRef(null);
  const logoFilesInputRef = useRef(null);

  // Local logo files dropped alongside the CSV — keyed by lower-cased filename
  // so CSV rows whose `logo_url` is just a filename can match.
  const [localLogos, setLocalLogos] = useState({}); // { "manchester-united.png": File }

  // Media Picker (used to attach a logo from the Media Library on the Add Team form)
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);

  // Bulk-select + bulk-delete
  const { bulkDeleteTeams } = useStore();
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAllFiltered = (allIds) => {
    setSelectedIds((prev) => {
      const everySelected = allIds.every((id) => prev.has(id));
      if (everySelected) {
        const next = new Set(prev);
        allIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      allIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const performBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const r = await bulkDeleteTeams(ids);
      toast.success(`Deleted ${r?.deleted ?? ids.length} team${ids.length === 1 ? "" : "s"}`);
      clearSelection();
      setConfirmBulkDelete(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleMediaPick = async (item) => {
    const newLogoUrl = item.url; // e.g. "/api/media/{id}/file"
    setFormData((prev) => ({ ...prev, logo: newLogoUrl }));
    // Auto-extract color from the picked logo if no color set yet
    if (!formData.primaryColor || formData.primaryColor === "#1e3a5f") {
      try {
        const color = await extractDominantColor(mediaUrl(newLogoUrl));
        setFormData((prev) => ({ ...prev, primaryColor: color }));
      } catch {
        // ignore
      }
    }
  };

  const filteredTeams = teams.filter(
    (team) =>
      team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.sport.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const dataUrl = reader.result;
        // Only auto-pick color if user hasn't customised yet (still default OR matches previous auto-pick)
        const userHasCustomized =
          editingTeam &&
          formData.primaryColor !== "#1e3a5f" &&
          formData.primaryColor !== editingTeam.primaryColor;

        setFormData((prev) => ({ ...prev, logo: dataUrl }));

        if (!userHasCustomized) {
          try {
            const color = await extractDominantColor(dataUrl);
            setFormData((prev) => ({ ...prev, logo: dataUrl, primaryColor: color }));
            toast.success("Auto-picked color from logo", {
              description: `${color.toUpperCase()} — feel free to tweak below`,
            });
          } catch {
            // Silent fail; user can still pick manually
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const [submitting, setSubmitting] = useState(false);

  // ---------- CSV bulk import ----------
  const parseCsv = (text) => {
    // Minimal CSV parser supporting quoted fields and commas inside quotes.
    const rows = [];
    let cur = [];
    let field = "";
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
        else if (c === '"') inQuotes = false;
        else field += c;
      } else {
        if (c === '"') inQuotes = true;
        else if (c === ",") { cur.push(field); field = ""; }
        else if (c === "\n") { cur.push(field); rows.push(cur); cur = []; field = ""; }
        else if (c === "\r") { /* ignore */ }
        else field += c;
      }
    }
    if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
    return rows.filter((r) => r.some((v) => String(v).trim().length > 0));
  };

  const onCsvPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    const text = await file.text();
    const allRows = parseCsv(text);
    // Strip comment lines starting with `#` (used in our sample CSV)
    const rows = allRows.filter((r) => !(r[0] && String(r[0]).trim().startsWith("#")));
    if (rows.length === 0) {
      toast.error("CSV is empty");
      return;
    }
    // Detect header row
    const header = rows[0].map((h) => String(h || "").trim().toLowerCase());
    const idx = {
      name: header.indexOf("name"),
      sport: header.indexOf("sport"),
      logo_url: ["logo_url", "logo", "logourl"].map((k) => header.indexOf(k)).find((i) => i >= 0) ?? -1,
      primaryColor: ["primarycolor", "color", "primary_color"].map((k) => header.indexOf(k)).find((i) => i >= 0) ?? -1,
      league_slug: ["league_slug", "league_id", "leagueslug", "league-slug"].map((k) => header.indexOf(k)).find((i) => i >= 0) ?? -1,
      league: ["league", "league_name", "competition"].map((k) => header.indexOf(k)).find((i) => i >= 0) ?? -1,
    };
    // sport is optional (default applied at submit time); name is required
    const hasHeader = idx.name >= 0;
    const dataRows = hasHeader ? rows.slice(1) : rows;

    const errors = [];
    const parsed = dataRows.map((r, rowIdx) => {
      const get = (i) => (i >= 0 && i < r.length ? String(r[i] || "").trim() : "");
      let name, sport, logo_url, primaryColor, league_slug, league;
      if (hasHeader) {
        name = get(idx.name);
        sport = get(idx.sport);
        logo_url = get(idx.logo_url);
        primaryColor = get(idx.primaryColor);
        league_slug = get(idx.league_slug);
        league = get(idx.league);
      } else {
        // Positional: name, sport, logo_url, primaryColor, league_slug
        [name, sport, logo_url, primaryColor, league_slug] =
          [r[0], r[1], r[2], r[3], r[4]].map((v) => String(v || "").trim());
        league = "";
      }
      if (!name) {
        errors.push({ row: rowIdx + 1, error: `Missing name on row ${rowIdx + 1}` });
      }
      return { name, sport, logo_url, primaryColor, league_slug, league };
    });

    setCsvRows(parsed);
    setCsvErrors(errors);
    if (errors.length > 0) {
      toast.warning(`${errors.length} row${errors.length === 1 ? "" : "s"} have issues`, {
        description: "Rows missing name/sport will be skipped.",
      });
    } else {
      toast.success(`${parsed.length} rows ready to import`);
    }
  };

  const handleImportCsv = async () => {
    // Apply default sport for any row that doesn't specify one.
    const valid = csvRows
      .filter((r) => r.name)
      .map((r) => ({ ...r, sport: r.sport || defaultSport }));

    if (valid.length === 0) {
      toast.error("No valid rows to import");
      return;
    }
    setImporting(true);
    try {
      // ---- Phase 1: upload local logo files referenced by the CSV ----
      // For every row whose logo_url matches a filename we have locally, queue
      // that file. After upload, replace logo_url with the resulting media URL.
      const filenameToFile = { ...localLogos };
      const rowsToUpload = [];
      for (const row of valid) {
        const ref = (row.logo_url || "").trim();
        if (!ref) continue;
        // Strip any path prefix the user typed (logos/foo.png → foo.png)
        const basename = ref.split(/[\\/]/).pop().toLowerCase();
        const file = filenameToFile[basename];
        if (file && !rowsToUpload.find((r) => r.basename === basename)) {
          rowsToUpload.push({ basename, file });
        }
      }

      const filenameToMediaUrl = {};
      if (rowsToUpload.length > 0) {
        setImportProgress({
          phase: `Uploading ${rowsToUpload.length} logo file${rowsToUpload.length === 1 ? "" : "s"} to Media Library...`,
          done: 0, total: rowsToUpload.length,
        });
        // Upload in chunks of 25 (matches MediaPage limit).
        const CHUNK = 25;
        for (let i = 0; i < rowsToUpload.length; i += CHUNK) {
          const chunk = rowsToUpload.slice(i, i + CHUNK);
          const fd = new FormData();
          chunk.forEach((c) => fd.append("files", c.file, c.file.name));
          try {
            const { data: items } = await api.post("/media/upload", fd, {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 180_000,
            });
            // The server keeps file order and returns one MediaItem per upload.
            chunk.forEach((c, idx) => {
              const item = items[idx];
              if (item?.url) filenameToMediaUrl[c.basename] = item.url;
            });
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Some logo files failed to upload");
          }
          setImportProgress((p) => ({ ...p, done: Math.min(p.done + chunk.length, p.total) }));
        }
      }

      // Replace any logo_url that pointed to a local filename with its uploaded media URL.
      const rowsForApi = valid.map((row) => {
        const ref = (row.logo_url || "").trim();
        if (!ref) return row;
        const basename = ref.split(/[\\/]/).pop().toLowerCase();
        if (filenameToMediaUrl[basename]) {
          return { ...row, logo_url: filenameToMediaUrl[basename] };
        }
        return row;
      });

      // ---- Phase 2: bulk-import (server fetches any remaining http(s) URLs in parallel) ----
      setImportProgress({ phase: "Importing teams...", done: 0, total: rowsForApi.length });
      const { data: result } = await api.post("/teams/bulk-import", {
        rows: rowsForApi,
        auto_create_leagues: autoCreateLeagues,
        default_league_logo_url: defaultLeagueLogoUrl,
        default_league_color: defaultLeagueColor,
        default_league_secondary_color: defaultLeagueSecondaryColor,
      });
      const created = result?.created || [];
      const leaguesCreated = result?.leagues_created || [];
      const apiErrors = result?.errors || [];

      // Refresh the store so newly-created teams + leagues appear immediately.
      await useStore.getState().hydrate();

      toast.success(
        `Imported ${created.length} team${created.length === 1 ? "" : "s"}`
        + (leaguesCreated.length > 0 ? ` · ${leaguesCreated.length} new league${leaguesCreated.length === 1 ? "" : "s"}` : ""),
      );
      if (apiErrors.length > 0) {
        toast.warning(`${apiErrors.length} row${apiErrors.length === 1 ? "" : "s"} had warnings (e.g., logo fetch failed)`);
      }

      // Client-side auto color extraction for teams without explicit color.
      const needsColor = created.filter(
        (t, i) => t.logo && (!valid[i].primaryColor) && (t.primaryColor === "#1e3a5f" || !t.primaryColor)
      );
      if (needsColor.length > 0) {
        setImportProgress({ phase: "Auto-extracting colors...", done: 0, total: needsColor.length });
        for (let i = 0; i < needsColor.length; i++) {
          const t = needsColor[i];
          try {
            const color = await extractDominantColor(t.logo);
            await api.put(`/teams/${t.id}`, {
              name: t.name, sport: t.sport, logo: t.logo, primaryColor: color,
            });
            useStore.setState((state) => ({
              teams: state.teams.map((x) => (x.id === t.id ? { ...x, primaryColor: color } : x)),
            }));
          } catch {
            // Skip silently
          }
          setImportProgress((p) => ({ ...p, done: i + 1 }));
        }
        toast.success(`Auto-picked colors for ${needsColor.length} team${needsColor.length === 1 ? "" : "s"}`);
      }

      // Reset and close
      setCsvRows([]);
      setCsvErrors([]);
      setCsvFileName("");
      setIsImportOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      setImportProgress({ phase: "", done: 0, total: 0 });
    }
  };

  const downloadSampleCsv = () => {
    const sample = [
      "name,sport,logo_url,primaryColor,league_slug",
      "# logo_url can be: a public https URL, a Media Library URL, or just a filename",
      "# (in which case drop the matching image file in the 'logo files' picker below).",
      "Manchester United,Football,manchester-united.png,,premier-league",
      "Liverpool FC,Football,https://upload.wikimedia.org/wikipedia/en/0/0c/Liverpool_FC.svg,#c8102e,premier-league",
      "Real Madrid,Football,,,la-liga",
    ].join("\n");
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "teams-sample.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const resetImport = () => {
    setCsvRows([]);
    setCsvErrors([]);
    setCsvFileName("");
    setLocalLogos({});
    if (csvInputRef.current) csvInputRef.current.value = "";
    if (logoFilesInputRef.current) logoFilesInputRef.current.value = "";
  };

  // Dual-drop support: the user can drop a folder of logo files alongside the CSV.
  // We index them by basename (lower-cased) so a CSV row with logo_url='liverpool.png'
  // will pick up the matching File and upload it to /media before bulk-import.
  const onLogoFilesPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const map = { ...localLogos };
    let added = 0;
    for (const f of files) {
      // webkitRelativePath includes folder name when picking a directory; strip it.
      const base = (f.name || "").toLowerCase();
      if (!f.type.startsWith("image/")) continue;
      if (f.size > 10 * 1024 * 1024) {
        toast.warning(`Skipped ${f.name} — exceeds 10MB`);
        continue;
      }
      map[base] = f;
      added++;
    }
    setLocalLogos(map);
    toast.success(`Loaded ${added} logo file${added === 1 ? "" : "s"}`, {
      description: "They'll be uploaded to Media Library when you click Import.",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.sport) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      if (editingTeam) {
        await updateTeam(editingTeam.id, formData);
        toast.success("Team updated successfully");
      } else {
        await addTeam(formData);
        toast.success("Team added successfully");
      }
      setFormData({ name: "", sport: "", logo: "", primaryColor: "#1e3a5f" });
      setEditingTeam(null);
      setIsDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save team");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setFormData({ name: team.name, sport: team.sport, logo: team.logo, primaryColor: team.primaryColor || "#1e3a5f" });
    setIsDialogOpen(true);
  };

  const handleDelete = async (teamId) => {
    try {
      await deleteTeam(teamId);
      toast.success("Team deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete team");
    }
  };

  const openNewTeamDialog = () => {
    setEditingTeam(null);
    setFormData({ name: "", sport: "", logo: "", primaryColor: "#1e3a5f" });
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Teams Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Add and manage teams with their logos for thumbnail generation
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Dialog open={isImportOpen} onOpenChange={(open) => { setIsImportOpen(open); if (!open) resetImport(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="csv-import-trigger">
                <FileSpreadsheet className="w-4 h-4" />
                Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="csv-import-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Bulk Import Teams</DialogTitle>
                <DialogDescription>
                  CSV columns: <code className="text-xs">name, sport, logo_url, primaryColor, league_slug</code>
                  {" "}(header optional · only <code>name</code> is required).
                  Logos are fetched server-side in parallel; colors auto-picked when missing.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={onCsvPick}
                    className="hidden"
                    data-testid="csv-file-input"
                  />
                  <Button variant="outline" onClick={() => csvInputRef.current?.click()} disabled={importing}>
                    <Upload className="w-4 h-4 mr-2" />
                    {csvFileName ? "Replace CSV" : "Choose CSV file"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={downloadSampleCsv} disabled={importing}>
                    Download sample
                  </Button>
                  {csvFileName && (
                    <span className="text-xs text-muted-foreground">{csvFileName} · {csvRows.length} rows</span>
                  )}
                </div>

                {/* Optional: drop a folder of logo files alongside the CSV */}
                <div className="rounded-lg border border-dashed border-border p-3 space-y-1.5 bg-secondary/20">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <p className="text-xs font-medium text-foreground">Optional: drop logo files from your computer</p>
                      <p className="text-[11px] text-muted-foreground">
                        For each row whose <code>logo_url</code> is just a filename (e.g. <code>arizona-arsenal.png</code>),
                        pick the matching image here and we'll upload it to the Media Library before importing.
                      </p>
                    </div>
                    <input
                      ref={logoFilesInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={onLogoFilesPick}
                      className="hidden"
                      data-testid="csv-logo-files-input"
                    />
                    <Button
                      variant="outline" size="sm"
                      onClick={() => logoFilesInputRef.current?.click()}
                      disabled={importing}
                    >
                      <Upload className="w-3.5 h-3.5 mr-1.5" />
                      {Object.keys(localLogos).length === 0
                        ? "Pick logo files"
                        : `${Object.keys(localLogos).length} loaded · Add more`}
                    </Button>
                    {Object.keys(localLogos).length > 0 && (
                      <Button
                        variant="ghost" size="sm" className="text-xs"
                        onClick={() => { setLocalLogos({}); if (logoFilesInputRef.current) logoFilesInputRef.current.value = ""; }}
                        disabled={importing}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Default sport + league config */}
                <div className="grid sm:grid-cols-2 gap-3 p-3 rounded-lg border border-border bg-secondary/30">
                  <div className="space-y-1">
                    <Label className="text-xs">Default sport (used when CSV lacks `sport` column)</Label>
                    <Select value={defaultSport} onValueChange={setDefaultSport}>
                      <SelectTrigger className="h-9" data-testid="csv-default-sport"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 flex items-end gap-2">
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoCreateLeagues}
                        onChange={(e) => setAutoCreateLeagues(e.target.checked)}
                        data-testid="csv-auto-leagues"
                        className="w-4 h-4"
                      />
                      Auto-create leagues from <code>league_slug</code>
                    </label>
                  </div>
                  {autoCreateLeagues && (
                    <>
                      <div className="space-y-1 sm:col-span-2">
                        <Label className="text-xs">Default league logo URL (applied to every new league)</Label>
                        <Input
                          value={defaultLeagueLogoUrl}
                          onChange={(e) => setDefaultLeagueLogoUrl(e.target.value)}
                          placeholder="https://... or paste a Media Library URL"
                          className="h-9"
                          data-testid="csv-league-logo-url"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Primary color</Label>
                        <div className="flex gap-2 items-center">
                          <input type="color" value={defaultLeagueColor}
                            onChange={(e) => setDefaultLeagueColor(e.target.value)}
                            className="w-9 h-9 rounded border border-border cursor-pointer" />
                          <Input value={defaultLeagueColor}
                            onChange={(e) => setDefaultLeagueColor(e.target.value)}
                            className="h-9 flex-1" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Secondary color</Label>
                        <div className="flex gap-2 items-center">
                          <input type="color" value={defaultLeagueSecondaryColor}
                            onChange={(e) => setDefaultLeagueSecondaryColor(e.target.value)}
                            className="w-9 h-9 rounded border border-border cursor-pointer" />
                          <Input value={defaultLeagueSecondaryColor}
                            onChange={(e) => setDefaultLeagueSecondaryColor(e.target.value)}
                            className="h-9 flex-1" />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {csvRows.length > 0 && (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-secondary sticky top-0">
                          <tr className="text-left">
                            <th className="px-3 py-2 font-medium">Name</th>
                            <th className="px-3 py-2 font-medium">Sport</th>
                            <th className="px-3 py-2 font-medium">League</th>
                            <th className="px-3 py-2 font-medium">Logo source</th>
                            <th className="px-3 py-2 font-medium">Color</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvRows.slice(0, 100).map((r, i) => {
                            const ref = (r.logo_url || "").trim();
                            const basename = ref.split(/[\\/]/).pop().toLowerCase();
                            const isLocal = !!localLogos[basename];
                            const isHttp = /^https?:\/\//i.test(ref);
                            const isMedia = ref.startsWith("/api/media/");
                            let logoLabel, logoClass;
                            if (!ref) { logoLabel = "—"; logoClass = "text-muted-foreground"; }
                            else if (isLocal) { logoLabel = `📁 ${basename} (local)`; logoClass = "text-emerald-400"; }
                            else if (isHttp) { logoLabel = `🌐 ${ref}`; logoClass = "text-muted-foreground"; }
                            else if (isMedia) { logoLabel = `📚 Media Library`; logoClass = "text-muted-foreground"; }
                            else { logoLabel = `❓ ${ref} (no match)`; logoClass = "text-amber-400"; }
                            return (
                              <tr key={i} className={cn("border-t border-border", !r.name && "bg-destructive/10")}>
                                <td className="px-3 py-1.5 truncate max-w-[140px]" title={r.name}>{r.name || <span className="text-destructive">missing</span>}</td>
                                <td className="px-3 py-1.5 text-muted-foreground">{r.sport || <span className="opacity-60">{defaultSport}</span>}</td>
                                <td className="px-3 py-1.5 truncate max-w-[150px] text-muted-foreground" title={r.league_slug || r.league}>{r.league_slug || r.league || "—"}</td>
                                <td className={cn("px-3 py-1.5 truncate max-w-[210px]", logoClass)} title={ref || ""}>{logoLabel}</td>
                                <td className="px-3 py-1.5">
                                  {r.primaryColor ? (
                                    <span className="inline-flex items-center gap-1.5">
                                      <span className="w-3 h-3 rounded-full border border-border" style={{ backgroundColor: r.primaryColor }} />
                                      {r.primaryColor}
                                    </span>
                                  ) : <span className="text-muted-foreground">auto-pick</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {csvRows.length > 100 && (
                      <div className="px-3 py-1.5 text-[11px] text-muted-foreground bg-secondary/50 border-t border-border">
                        Showing first 100 of {csvRows.length} rows. All will be imported.
                      </div>
                    )}
                  </div>
                )}

                {csvErrors.length > 0 && (
                  <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                    {csvErrors.length} row{csvErrors.length === 1 ? "" : "s"} will be skipped due to missing name or sport.
                  </div>
                )}

                {importing && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {importProgress.phase}
                    {importProgress.total > 0 && ` (${importProgress.done}/${importProgress.total})`}
                  </div>
                )}
              </div>

              <DialogFooter className="sticky bottom-0 bg-background pt-3 -mx-6 px-6 -mb-6 pb-6 border-t border-border">
                <Button type="button" variant="outline" size="sm" onClick={() => setIsImportOpen(false)} disabled={importing}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="premium"
                  onClick={handleImportCsv}
                  disabled={importing || csvRows.filter((r) => r.name).length === 0}
                  data-testid="csv-import-submit"
                >
                  {importing ? "Importing..." : `Import ${csvRows.filter((r) => r.name).length} team${csvRows.filter((r) => r.name).length === 1 ? "" : "s"}`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="premium" className="gap-2" onClick={openNewTeamDialog}>
              <Plus className="w-4 h-4" />
              Add Team
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">
                {editingTeam ? "Edit Team" : "Add New Team"}
              </DialogTitle>
              <DialogDescription>
                {editingTeam
                  ? "Update the team details below"
                  : "Enter the team details and upload their logo"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Logo Upload & Team Info in Row */}
              <div className="flex gap-4 items-start">
                {/* Logo Upload */}
                <div className="flex flex-col items-center flex-shrink-0 gap-1.5">
                  <div
                    className={cn(
                      "w-20 h-20 rounded-xl border-2 border-dashed flex items-center justify-center cursor-pointer overflow-hidden",
                      "transition-all duration-200 hover:border-primary/50 hover:bg-primary/5",
                      formData.logo ? "border-primary/30 bg-primary/5" : "border-border"
                    )}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {formData.logo ? (
                      <img
                        src={mediaUrl(formData.logo)}
                        alt="Team logo"
                        className="w-full h-full object-contain p-1"
                       loading="lazy" decoding="async"/>
                    ) : (
                      <div className="text-center p-2">
                        <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-1" />
                        <p className="text-[10px] text-muted-foreground">Logo</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="h-6 text-[10px] px-2"
                    onClick={() => setIsMediaPickerOpen(true)}
                    data-testid="team-pick-from-media"
                  >
                    <FolderOpen className="w-3 h-3 mr-1" />
                    Pick from Library
                  </Button>
                </div>

                {/* Team Name & Sport */}
                <div className="flex-1 space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="name" className="text-xs">Team Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Manchester United"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, name: e.target.value }))
                      }
                      className="input-focus h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="sport" className="text-xs">Sport *</Label>
                    <Select
                      value={formData.sport}
                      onValueChange={(value) =>
                        setFormData((prev) => ({ ...prev, sport: value }))
                      }
                    >
                      <SelectTrigger className="input-focus h-9">
                        <SelectValue placeholder="Select sport" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPORTS.map((sport) => (
                          <SelectItem key={sport} value={sport}>
                            {sport}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Primary Color */}
              <div className="space-y-1">
                <Label className="text-xs">Team Primary Color</Label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={formData.primaryColor}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, primaryColor: e.target.value }))
                    }
                    className="w-9 h-9 rounded cursor-pointer border border-border"
                  />
                  <Input
                    value={formData.primaryColor}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, primaryColor: e.target.value }))
                    }
                    className="flex-1 h-9"
                    placeholder="#1e3a5f"
                  />
                  <span className="text-xs text-muted-foreground">For gradients</span>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" size="sm" onClick={() => setIsDialogOpen(false)} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={submitting} data-testid="team-submit-button">
                  {submitting ? "Saving..." : editingTeam ? "Save Changes" : "Add Team"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search teams by name or sport..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-secondary/50 border-border/50"
          />
        </div>

        {/* Bulk action toolbar */}
        {filteredTeams.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
              <Checkbox
                checked={
                  filteredTeams.length > 0 &&
                  filteredTeams.every((t) => selectedIds.has(t.id))
                }
                onCheckedChange={() => toggleSelectAllFiltered(filteredTeams.map((t) => t.id))}
                data-testid="teams-select-all"
              />
              Select all ({selectedIds.size}/{filteredTeams.length})
            </label>
            {selectedIds.size > 0 && (
              <>
                <Button
                  variant="ghost" size="sm" className="h-8 text-xs"
                  onClick={clearSelection}
                  data-testid="teams-clear-selection"
                >
                  Clear
                </Button>
                <Button
                  variant="destructive" size="sm" className="h-8 gap-1.5"
                  onClick={() => setConfirmBulkDelete(true)}
                  data-testid="teams-bulk-delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete {selectedIds.size}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Teams Grid */}
      {filteredTeams.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredTeams.map((team) => {
            const isSelected = selectedIds.has(team.id);
            return (
            <Card
              key={team.id}
              className={cn(
                "glass-card group relative",
                isSelected && "ring-2 ring-primary",
              )}
              data-testid={`team-card-${team.id}`}
            >
              <div
                className={cn(
                  "absolute top-2 left-2 z-10 transition-opacity",
                  isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                )}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleSelect(team.id)}
                  className="bg-card border-border"
                  data-testid={`team-select-${team.id}`}
                />
              </div>
              <CardContent className="p-4">
                <div
                  className="team-logo w-20 h-20 mx-auto mb-3"
                  style={{
                    borderColor: team.primaryColor,
                    boxShadow: `0 0 20px ${team.primaryColor}20`,
                  }}
                >
                  {team.logo ? (
                    <img src={mediaUrl(team.logo)} alt={team.name}  loading="lazy" decoding="async"/>
                  ) : (
                    <ImageIcon className="w-8 h-8 text-muted-foreground" />
                  )}
                </div>
                <h3 className="text-sm font-semibold text-foreground text-center truncate">
                  {team.name}
                </h3>
                <p className="text-xs text-muted-foreground text-center mt-1">
                  {team.sport}
                </p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div
                    className="w-4 h-4 rounded-full border border-border"
                    style={{ backgroundColor: team.primaryColor }}
                  />
                </div>
                <div className="flex items-center justify-center gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => handleEdit(team)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(team.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      ) : (
        <Card className="glass-card">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <ImageIcon className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No teams yet
            </h3>
            <p className="text-muted-foreground mb-4 max-w-sm mx-auto">
              Start by adding teams with their logos. These will be used when
              generating thumbnails.
            </p>
            <Button variant="premium" className="gap-2" onClick={openNewTeamDialog}>
              <Plus className="w-4 h-4" />
              Add Your First Team
            </Button>
          </CardContent>
        </Card>
      )}

      <MediaPicker
        open={isMediaPickerOpen}
        onOpenChange={setIsMediaPickerOpen}
        onSelect={handleMediaPick}
        title="Pick a team logo"
      />

      <AlertDialog open={confirmBulkDelete} onOpenChange={(open) => !bulkDeleting && setConfirmBulkDelete(open)}>
        <AlertDialogContent data-testid="teams-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedIds.size} team{selectedIds.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the selected team{selectedIds.size === 1 ? "" : "s"} from the database
              and from any league&apos;s roster. Existing matches that reference these teams keep their
              snapshot names but will lose the team logo. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performBulkDelete}
              disabled={bulkDeleting}
              data-testid="teams-bulk-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDeleting ? "Deleting..." : `Delete ${selectedIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
